import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Building2, ArrowRight, Users, Mail } from "lucide-react";
import PublicDomainCard from "@/pages/admin/PublicDomainCard";
import WeeklyBillingCard from "@/pages/admin/WeeklyBillingCard";
import BackupsCard from "@/pages/admin/BackupsCard";
import SmsStatsCard from "@/pages/admin/SmsStatsCard";
import TextsCard from "@/pages/admin/TextsCard";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isSuper = user?.role === "superadmin";
  const [svjs, setSvjs] = useState(null);
  const [teamLoad, setTeamLoad] = useState([]);

  useEffect(() => {
    api.get("/svjs").then((r) => setSvjs(r.data)).catch((e) => toast.error(apiErr(e)));
    if (isSuper) api.get("/overview").then((r) => setTeamLoad(r.data.team_load)).catch(() => {});
  }, [isSuper]);

  const inviteAll = async () => {
    try {
      const { data } = await api.post("/svjs/invite-all-chairmen");
      toast.success(data.count ? `Pozvánky odeslány (${data.count}): ${data.sent_to.join(", ")}` : "Všichni předsedové již mají aktivní účet");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!svjs) return <p className="text-slate-500">Načítání…</p>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2540]">Přehled</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">{isSuper ? "Správa všech SVJ a servisních akcí" : "Vaše společenství vlastníků"}</p>
        </div>
        {isSuper && <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={inviteAll} data-testid="invite-all-btn"><Mail className="h-4 w-4 mr-2" /> Pozvat všechny předsedy</Button>
          <Button size="sm" onClick={() => nav("/admin/svj/new")} style={{ background: "#0A2540" }} data-testid="create-svj-btn">
            <Building2 className="h-4 w-4 mr-2" /> Založit SVJ</Button>
        </div>}
      </div>

      {isSuper && <PublicDomainCard />}
      {isSuper && <TextsCard />}
      {isSuper && <WeeklyBillingCard initialWeekEnd={new URLSearchParams(window.location.search).get("fakturace")} />}
      {isSuper && <SmsStatsCard />}
      {isSuper && <BackupsCard />}

      {isSuper && teamLoad.length > 0 && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-[#0A2540] flex items-center gap-2 mb-3"><Users className="h-4 w-4" /> Vytížení týmů</h3>
          <div className="flex flex-wrap gap-3">
            {teamLoad.map((t) => (
              <div key={t.team} className="px-4 py-2 rounded-lg bg-slate-50 border text-sm">
                <span className="font-medium">{t.team}</span> · {t.days_assigned} dnů
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {svjs.map((s) => (
          <button key={s.id} onClick={() => nav(`/admin/svj/${s.id}`)} data-testid={`svj-card-${s.id}`}
            className="text-left bg-white rounded-xl border p-5 hover:shadow-md hover:border-[#0A2540] transition-all">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-lg grid place-items-center" style={{ background: "#0A2540" }}>
                <Building2 className="h-5 w-5" style={{ color: "#C5A059" }} />
              </div>
              {s.archived && <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">Archiv</span>}
            </div>
            <div className="mt-3 font-bold text-lg text-[#0A2540]">{s.name}</div>
            <div className="text-slate-500 text-sm">{s.address}</div>
            <div className="mt-3 flex gap-4 text-sm text-slate-600">
              <span>{s.entrances} vchodů</span><span>{s.apartments} bytů</span><span>{s.actions} akcí</span>
            </div>
            <div className="mt-3 text-[#0A2540] text-sm font-medium inline-flex items-center">Otevřít <ArrowRight className="h-4 w-4 ml-1" /></div>
          </button>
        ))}
        {svjs.length === 0 && <p className="text-slate-500">Zatím žádná SVJ.</p>}
      </div>
    </div>
  );
}
